import React from 'react';

import { useAppFieldContext } from '@/app/lib/tanstack-form';
import { FieldInfo } from '@/common/components/field-info';
import { Description, Field, Label } from '@/common/components/fieldset';
import { Input } from '@/common/components/input';

export type FormInputProps = {
  /** The label content for the input field. Can be a string or JSX element. */
  label: React.ReactNode;
  /** Optional description text shown below the label to provide additional context. */
  description?: React.ReactNode;
  /** The HTML input type. Defaults to 'text'. */
  type?: 'date' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url';
  /** Placeholder text shown when the input is empty. */
  placeholder?: string;
  /** Additional CSS classes to apply to the input element. */
  className?: string;
  /** Step value for number inputs. */
  step?: string | number;
};

/**
 * A form input component that integrates with TanStack Form using useFieldContext.
 *
 * This component provides a complete form field with label, optional description,
 * input control, and automatic error display. It uses Catalyst UI components for
 * consistent styling and accessibility.
 *
 * @example
 * ```tsx
 * <form.Field name="email">
 *   <FormInput
 *     label="Email Address"
 *     description="We'll never share your email"
 *     type="email"
 *     placeholder="Enter your email"
 *   />
 * </form.Field>
 * ```
 */
export function FormInput({
  label,
  description,
  type = 'text',
  placeholder,
  className,
  step
}: FormInputProps) {
  const field = useAppFieldContext<string>();

  return (
    <Field>
      <Label htmlFor={field.name}>{label}</Label>
      {description && <Description>{description}</Description>}
      <Input
        id={field.name}
        name={field.name}
        type={type}
        value={field.state.value}
        placeholder={placeholder}
        step={step}
        onBlur={field.handleBlur}
        onChange={event => field.handleChange(event.target.value)}
        invalid={field.state.meta.isTouched && !field.state.meta.isValid}
        className={className}
      />
      <FieldInfo field={field} />
    </Field>
  );
}
